// Shareable links for the WSJF calculator: the jobs with their score ranges and the
// size of the top group, in `#wsjf=…` (see src/lib/share.js).

import { defineShare, list, num, obj, str } from "../lib/share.js";
import { MAX_JOBS, PARTS } from "./model.js";

const MAX_SCORE = 1e4;
const range = (raw) => {
  const o = obj(raw);
  return { low: num(o.low, NaN, 0, MAX_SCORE), high: num(o.high, NaN, 0, MAX_SCORE) };
};

export const wsjfShare = defineShare({
  key: "wsjf",
  version: 1,
  docs: {
    summary: "Rank jobs by weighted shortest job first (cost of delay over job size) with every score given as a range, and see how sure each place in the order is.",
    fields: {
      top: "How many places count as the top group, for the chance each job is in it (1 to the number of jobs).",
      jobs: `Up to ${MAX_JOBS} jobs: label, and four ranges { low, high }, all 0 or more: value (business value), time (time criticality), risk (risk reduction or opportunity enablement) and size (job size, above 0).`,
    },
    caveats: [
      "Scores are relative judgements; the ranking says nothing about absolute value.",
      "Cost-of-delay parts are read as uniform over their ranges and size as a 90% lognormal range; scores are treated as independent.",
    ],
  },
  toState: (s) => ({ top: s.top, jobs: s.jobs.map((j) => ({ label: j.label, value: { ...j.value }, time: { ...j.time }, risk: { ...j.risk }, size: { ...j.size } })) }),
  fromState: (raw) => {
    const jobs = list(
      raw.jobs,
      (x) => {
        const o = obj(x);
        return { label: str(o.label, "Job", 120), ...Object.fromEntries([...PARTS, "size"].map((k) => [k, range(o[k])])) };
      },
      [],
      MAX_JOBS,
    );
    if (!jobs.length) throw new Error("jobs");
    return { top: Math.round(num(raw.top, 1, 1, MAX_JOBS)), jobs };
  },
});
